import { useEffect, useRef } from "react";

const SCROLL_THRESHOLD_PX = 150;

function getIsNearBottom(container: HTMLDivElement): boolean {
  const { scrollTop, scrollHeight, clientHeight } = container;
  const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
  return distanceFromBottom <= SCROLL_THRESHOLD_PX;
}

interface UseScrollToBottomOnNewMessageParams {
  messagesCount: number;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
}

/**
 * Hook to handle auto-scrolling to bottom on new messages.
 * Only scrolls if the user is already near the bottom of the container.
 */
export function useScrollToBottomOnNewMessage(params: UseScrollToBottomOnNewMessageParams): void {
  const { messagesCount, scrollContainerRef } = params;
  const previousMessagesCountRef = useRef(messagesCount);
  const wasNearBottomRef = useRef(true);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return undefined;
    }

    const handleScroll = () => {
      wasNearBottomRef.current = getIsNearBottom(container);
    };

    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [scrollContainerRef]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    const hasNewMessages = messagesCount > previousMessagesCountRef.current;
    previousMessagesCountRef.current = messagesCount;

    if (!hasNewMessages) {
      return;
    }

    if (!wasNearBottomRef.current) {
      return;
    }

    requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight;
      wasNearBottomRef.current = true;
    });
  }, [messagesCount, scrollContainerRef]);
}
